import {
  Box,
  Button,
  Card,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { organizationApi } from 'src/api/organization';
import { specializationApi } from 'src/api/specialization';
import { visitCategoriesApi } from 'src/api/visit-categories';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import SpecializationRemovalWarningModal from 'src/components/specialization/SpecializationRemovalWarningModal';
import ServiceFormAssociation from './ServiceFormAssociation';
import OrganizationServiceFormDialog from './OrganizationServiceFormDialog';
import {
  OrganizationServiceFormValues,
  servicePricingFields,
  toCreateServiceDto,
  toUpdateServiceDto,
} from './organizationServiceForm';
import { mapCategoryNameToVisitCategory } from 'src/utils/mapCategoryNameToVisitCategory';
import { mapSpecializationNameIdToString } from 'src/utils/mapSpecializationNameIdToString';
import { translateKey } from 'src/locales/translate';

type Props = {
  organizationId: string;
  enabledCategories: string[];
  visitType: string;
};

export default function OrganizationServiceManagement({
  organizationId,
  enabledCategories,
  visitType,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
  const [dialogDefaults, setDialogDefaults] =
    useState<OrganizationServiceFormValues | null>(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingServiceRemoval, setPendingServiceRemoval] = useState<
    string | null
  >(null);
  const [affectedProfessionals, setAffectedProfessionals] = useState<
    Array<{
      professionalName: string;
      affectedServices: Array<{
        serviceName: string;
        specializationName: string;
      }>;
    }>
  >([]);

  // Fetch all services to display what's configured
  const { data: servicesData } = useQuery(['services'], () =>
    specializationApi.specializationControllerGetAllServices(),
  );

  const allServices = servicesData?.data || [];

  // Fetch all specializations for the dropdown when creating new services
  const { data: specializationsData } = useQuery(['specializations'], () =>
    specializationApi.specializationControllerGetAllSpecializations(),
  );

  const allSpecializations = specializationsData?.data || [];
  const { data: categoriesData } = useQuery(['visitCategories'], () =>
    visitCategoriesApi.visitCategoriesControllerGetAllVisitCategories(),
  );

  const categories = categoriesData?.data || [];
  const categoryIdToNameMap = categories.reduce(
    (acc: Record<string, string>, cat: any) => {
      acc[cat.id || cat._id] = cat.name;
      return acc;
    },
    {},
  );

  // Fetch organization data to get enabledServices
  const { data: orgData } = useQuery(['organization', organizationId], () =>
    organizationApi.organizationControllerGetOrganizationById({
      id: organizationId,
    }),
  );

  const organization = orgData?.data;
  const enabledServiceIds = organization?.enabledServices || [];

  // Get enabled services with details
  const enabledServices = allServices.filter((service: any) =>
    enabledServiceIds.includes(service._id || service.id),
  );

  // Group services by category and visitType
  const servicesByCategory = enabledCategories.reduce((acc, categoryId) => {
    acc[categoryId] = enabledServices.filter(
      (service: any) =>
        service.category === categoryId && service.visitType === visitType,
    );
    return acc;
  }, {} as Record<string, typeof allServices>);

  // Mutation to create and add service
  const addServiceMutation = useMutation(
    async (data: {
      categoryId: string;
      service: OrganizationServiceFormValues;
    }) => {
      const response =
        await specializationApi.specializationControllerCreateOrganizationService(
          {
            organizationId,
            createSpecializationServiceDto: toCreateServiceDto(
              data.service,
              data.categoryId,
              visitType,
            ),
          },
        );

      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['services']);
        queryClient.invalidateQueries(['organization', organizationId]);
        queryClient.invalidateQueries(['specializations']);
        enqueueSnackbar(translateKey("hardcoded.usluga_dodana_pomyslnie"), { variant: 'success' });
        handleCloseDialog();
      },
      onError: (error: any) => {
        console.error('Error creating service:', error);
        enqueueSnackbar(
          error.response?.data?.message || translateKey("hardcoded.blad_podczas_dodawania_uslugi"),
          { variant: 'error' },
        );
      },
    },
  );

  // Mutation to update service
  const updateServiceMutation = useMutation(
    async (data: {
      serviceId: string;
      categoryId: string;
      service: OrganizationServiceFormValues;
    }) => {
      const response =
        await specializationApi.specializationControllerUpdateOrganizationService(
          {
            organizationId,
            serviceId: data.serviceId,
            updateSpecializationServiceDto: toUpdateServiceDto(
              data.service,
              data.categoryId,
              visitType,
            ),
          },
        );

      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['services']);
        queryClient.invalidateQueries(['organization', organizationId]);
        enqueueSnackbar(translateKey("hardcoded.usluga_zaktualizowana_pomyslnie"), {
          variant: 'success',
        });
        handleCloseDialog();
      },
      onError: (error: any) => {
        console.error('Error updating service:', error);
        enqueueSnackbar(
          error.response?.data?.message || translateKey("hardcoded.blad_podczas_aktualizacji_uslugi"),
          { variant: 'error' },
        );
      },
    },
  );

  // Mutation to remove service
  const removeServiceMutation = useMutation(
    async (serviceId: string) => {
      const updatedServices = enabledServiceIds.filter(
        (id: string) => id !== serviceId,
      );
      return organizationApi.organizationControllerUpdateOrganization({
        id: organizationId,
        updateOrganizationDto: {
          enabledServices: updatedServices as any,
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['organization', organizationId]);
        enqueueSnackbar(translateKey("hardcoded.usluga_usunieta_pomyslnie"), { variant: 'success' });
      },
      onError: () => {
        enqueueSnackbar(translateKey("hardcoded.blad_podczas_usuwania_uslugi"), { variant: 'error' });
      },
    },
  );

  const handleOpenDialog = (categoryId: string, service?: any) => {
    setSelectedCategory(categoryId);
    if (service) {
      setEditingServiceId(service._id || service.id);
      setDialogDefaults({
        specializationId:
          service.specializationId?._id ||
          service.specializationId?.id ||
          service.specializationId,
        name: service.name,
        duration: service.defaultDuration,
        price: service.defaultPrice,
        prepaidAmount: service.prepaidAmount ?? '',
        description: service.description || '',
      });
    } else {
      setEditingServiceId(null);
      setDialogDefaults({
        specializationId: '',
        name: '',
        duration: 30,
        price: servicePricingFields(visitType, categoryId).showsPrice ? '' : 0,
        prepaidAmount: '',
        description: '',
      });
    }
  };

  const handleCloseDialog = () => {
    setSelectedCategory('');
    setEditingServiceId(null);
    setDialogDefaults(null);
  };

  const handleSubmit = (values: OrganizationServiceFormValues) => {
    if (editingServiceId) {
      updateServiceMutation.mutate({
        serviceId: editingServiceId,
        categoryId: selectedCategory,
        service: values,
      });
    } else {
      addServiceMutation.mutate({
        categoryId: selectedCategory,
        service: values,
      });
    }
  };

  const handleRemoveService = async (serviceId: string) => {
    try {
      const response =
        await organizationApi.organizationControllerCheckOrganizationServiceUsage(
          {
            id: organizationId,
            checkServiceUsageDto: {
              serviceIds: [serviceId],
            },
          },
        );

      if (response.data?.hasAssignedServices) {
        setAffectedProfessionals(response.data.professionals);
        setPendingServiceRemoval(serviceId);
        setShowWarningModal(true);
        return;
      }
    } catch (error) {
      console.error('Failed to check service usage:', error);
    }

    removeServiceMutation.mutate(serviceId);
  };

  const handleConfirmRemoval = () => {
    if (pendingServiceRemoval) {
      removeServiceMutation.mutate(pendingServiceRemoval);
      setShowWarningModal(false);
      setPendingServiceRemoval(null);
      setAffectedProfessionals([]);
    }
  };

  const handleCancelRemoval = () => {
    setShowWarningModal(false);
    setPendingServiceRemoval(null);
    setAffectedProfessionals([]);
  };

  // Get organization's specializations filtered by selected category
  // Only calculate when dialog is open
  const availableSpecializations =
    !selectedCategory || !organization
      ? []
      : (() => {
          // Filter specializations by category that are in organization's list
          const orgSpecializationIds =
            (organization as any).specializations || [];

          const orgSpecIdStrings = orgSpecializationIds.map((id: any) =>
            typeof id === 'string' ? id : id?.$oid || id?.toString?.() || id,
          );

          const filtered = allSpecializations.filter((spec: any) => {
            const specId = spec.id || spec._id;
            const idMatch = orgSpecIdStrings.includes(specId);
            const specCategoryName =
              categoryIdToNameMap[spec.visitCategoryId] || spec.visitCategoryId;
            const categoryMatch = specCategoryName === selectedCategory;

            return idMatch && categoryMatch;
          });
          return filtered;
        })();

  return (
    <Box>
      {enabledCategories.map((categoryId) => {
        const categoryServices = servicesByCategory[categoryId] || [];
        const categoryName = mapCategoryNameToVisitCategory(categoryId as any);

        return (
          <Card
            key={categoryId}
            data-testid={`service-list-${visitType}-${categoryId}`}
            sx={{ p: 2, bgcolor: 'background.neutral' }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={categoryServices.length > 0 ? 2 : 1}
            >
              <Typography variant="subtitle2" fontWeight="medium">{translateKey("hardcoded.lista_uslug")}{categoryServices.length}{translateKey("hardcoded.uslug_2")}</Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={() => handleOpenDialog(categoryId)}
              >{translateKey("common.add")}</Button>
            </Box>

            {categoryServices.length === 0 ? (
              <Typography variant="caption" color="text.secondary">{translateKey("hardcoded.brak_uslug_kliknij_dodaj_aby_stworzyc_pierwsza_usluge")}</Typography>
            ) : (
              <Stack spacing={2}>
                {/* Group services by specialization */}
                {(() => {
                  // Group services by specializationId
                  const servicesBySpecialization = categoryServices.reduce(
                    (acc: any, service: any) => {
                      const specId =
                        service.specializationId?._id ||
                        service.specializationId?.id ||
                        service.specializationId;
                      if (!acc[specId]) {
                        acc[specId] = {
                          specializationName: service.specializationId
                            ? mapSpecializationNameIdToString(
                                service.specializationId,
                              )
                            : 'Nieznana specjalizacja',
                          services: [],
                        };
                      }
                      acc[specId].services.push(service);
                      return acc;
                    },
                    {},
                  );

                  return Object.entries(servicesBySpecialization).map(
                    ([specId, data]: [string, any]) => (
                      <Box key={specId}>
                        {/* Specialization header */}
                        <Typography
                          variant="caption"
                          fontWeight="medium"
                          color="text.secondary"
                          sx={{ mb: 1, display: 'block' }}
                        >
                          {data.specializationName}
                        </Typography>

                        {/* Services under this specialization */}
                        <Stack spacing={1}>
                          {data.services.map((service: any) => (
                            <Box
                              key={service._id || service.id}
                              sx={{
                                p: 1.5,
                                bgcolor: 'background.paper',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="flex-start"
                              >
                                <Box flex={1}>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    {service.name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mt: 0.5 }}
                                  >
                                    {service.defaultDuration} min
                                    {servicePricingFields(
                                      visitType,
                                      service.category,
                                    ).showsPrice &&
                                      translateKey('hardcoded.priceBullet', {
                                        price: service.defaultPrice,
                                      })}
                                  </Typography>
                                  {service.description && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        display: 'block',
                                        mt: 1,
                                        fontStyle: 'italic',
                                      }}
                                    >
                                      {service.description}
                                    </Typography>
                                  )}
                                  <ServiceFormAssociation
                                    serviceId={service._id || service.id}
                                    organizationId={organizationId}
                                  />
                                </Box>
                                <Box display="flex" gap={0.5}>
                                  <IconButton
                                    color="primary"
                                    aria-label={translateKey(
                                      'hardcoded.edytuj_usluge',
                                    )}
                                    onClick={() =>
                                      handleOpenDialog(categoryId, service)
                                    }
                                    size="small"
                                  >
                                    <Iconify icon="eva:edit-2-outline" />
                                  </IconButton>
                                  <IconButton
                                    color="error"
                                    aria-label={translateKey('common.remove')}
                                    onClick={() =>
                                      handleRemoveService(
                                        service._id || service.id,
                                      )
                                    }
                                    size="small"
                                  >
                                    <Iconify icon="eva:trash-2-outline" />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    ),
                  );
                })()}
              </Stack>
            )}
          </Card>
        );
      })}

      {dialogDefaults && (
        <OrganizationServiceFormDialog
          category={selectedCategory}
          categoryLabel={mapCategoryNameToVisitCategory(selectedCategory as any)}
          visitType={visitType}
          isEditing={!!editingServiceId}
          specializations={availableSpecializations}
          defaultValues={dialogDefaults}
          isSubmitting={
            addServiceMutation.isLoading || updateServiceMutation.isLoading
          }
          onClose={handleCloseDialog}
          onSubmit={handleSubmit}
        />
      )}

      <SpecializationRemovalWarningModal
        open={showWarningModal}
        onClose={handleCancelRemoval}
        onConfirm={handleConfirmRemoval}
        affectedServices={[]}
        isProfessional={false}
        affectedProfessionals={affectedProfessionals}
        title={translateKey("hardcoded.usuniecie_uslugi_z_placowki")}
        message={translateKey("hardcoded.serviceRemovalWarningMessage", { count: affectedProfessionals.length, professionals: affectedProfessionals.length === 1 ? translateKey("hardcoded.profesjonalisty") : translateKey("hardcoded.profesjonalistow") })}
      />
    </Box>
  );
}
